import { ContentfulPreviewAsset, ContentfulPreviewEntry } from '../../services/contentfulPreviewService'

import { overlayAsset } from './asset'
import { overlayBoolean } from './boolean'
import { overlayEntryLink } from './entryLink'
import { overlayJson } from './json'
import { overlayNumber } from './number'
import { overlayRichText } from './richText'
import { isRecord, UnknownRecord } from './shared'
import { overlayStringList } from './stringList'
import { overlayText } from './text'

const NO_ASSETS: ContentfulPreviewAsset[] = []

/**
 * Returns only the entries of `node` that this understands how to replace, leaving every other field
 * for the caller to keep at its build time value.
 */
const overlayableFields = (
  node: UnknownRecord,
  fields: UnknownRecord,
  fieldsById: Map<string, UnknownRecord>,
  assetsById: Map<string, ContentfulPreviewAsset>,
  entriesById: Map<string, ContentfulPreviewEntry>,
): UnknownRecord => {
  const overlaid: UnknownRecord = {}

  const overlayLinkedEntry = (
    linkedNode: UnknownRecord,
    linkedFields: UnknownRecord,
    linkedFieldsById: Map<string, UnknownRecord>,
  ) => overlayableFields(linkedNode, linkedFields, linkedFieldsById, assetsById, entriesById)

  for (const [key, value] of Object.entries(node)) {
    const field = fields[key]

    const next =
      overlayText(value, field) ??
      overlayStringList(value, field) ??
      overlayRichText(value, field, assetsById, entriesById) ??
      overlayBoolean(value, field) ??
      overlayNumber(value, field) ??
      // Before the entry and JSON handlers: an Asset link is a link, and an inline asset payload is
      // a plain object, so either would otherwise be mistaken for something it is not.
      overlayAsset(value, field, assetsById) ??
      overlayEntryLink(value, field, fieldsById, overlayLinkedEntry) ??
      overlayJson(value, field)

    if (next !== undefined) overlaid[key] = next
  }

  return overlaid
}

/**
 * Copies live Contentful field values onto a Gatsby query result, matching `contentful_id` against
 * `sys.id`.
 *
 * Handles Short text, Short text lists (e.g. table `cells`), Long text, Rich Text, booleans,
 * numbers, JSON/Object fields, Asset/image links, and single entry-link retargets when the new
 * entry is in the preview payload. Dates stay at their formatted build-time value.
 */
export const overlayContentfulFields = <T>(
  data: T,
  entries: ContentfulPreviewEntry[],
  assets: ContentfulPreviewAsset[] = NO_ASSETS,
): T => {
  if (!entries.length) return data

  const fieldsById = new Map(entries.map(entry => [entry.sys.id, entry.fields]))
  const assetsById = new Map(assets.map(asset => [asset.sys.id, asset]))
  // Full entries, not just their fields: rebuilding a rich-text entry embed needs
  // `sys.contentType` to know which Gatsby `__typename` it maps to.
  const entriesById = new Map(entries.map(entry => [entry.sys.id, entry]))

  const walk = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(walk)
    if (!isRecord(value)) return value

    const walked: UnknownRecord = {}
    for (const [key, nested] of Object.entries(value)) {
      walked[key] = walk(nested)
    }

    const fields = typeof value.contentful_id === 'string' ? fieldsById.get(value.contentful_id) : undefined

    return fields ? { ...walked, ...overlayableFields(walked, fields, fieldsById, assetsById, entriesById) } : walked
  }

  return walk(data) as unknown as T
}
